import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import Pagination from "react-js-pagination";
import { useSearchParams } from "react-router-dom";
import { IoSearch } from "react-icons/io5";
import NotFound from "../../components/not-found/NotFound";
import ModalBox from "../../components/modal/ModalBox";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import findSerialNumber from "../../utils/findSerialNumber";
import { toastMessage } from "../../utils/toastMessage";
import common from "../../routes/common";
import scrollToUp from "../../utils/scrollToUp";
import {
  fetchAstrologerRegistrationFormApi,
  updateAstrologerRegistrationFormStatusApi,
} from "../../services/consultantManagement";

const { NOT_FOUND } = common;

const SELECTION_STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Selected", value: "selected" },
  { label: "Rejected", value: "rejected" },
];

const formatDate = (value) => {
  if (!value) return "---";
  return new Date(value).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const getSelectionStatus = (form) => form?.selectionStatus || "pending";

const statusButtonStyle = (active, tone) => {
  const palettes = {
    selected: {
      background: active ? "#0f7b3a" : "#fff",
      color: active ? "#fff" : "#0f7b3a",
      border: "1px solid #0f7b3a",
    },
    rejected: {
      background: active ? "#b42318" : "#fff",
      color: active ? "#fff" : "#b42318",
      border: "1px solid #b42318",
    },
  };
  return {
    minWidth: 88,
    fontWeight: 700,
    padding: "6px 10px",
    borderRadius: 6,
    ...palettes[tone],
  };
};

const AstrologerRegistrationFormList = () => {
  const [searchParam, setSearchParam] = useSearchParams();
  const activePage = +searchParam.get("page") || 1;
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectionStatus, setSelectionStatus] = useState("");
  const debounceValue = useDebounce(search || "", 300);
  const [total, setTotal] = useState(0);
  const [formList, setFormList] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [updatingId, setUpdatingId] = useState("");
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const handlePageChange = (event) => {
    searchParam.set("page", event);
    setSearchParam(searchParam);
  };

  const getFormList = async (searchValue) => {
    try {
      showLoader();
      const sendData = {
        page: `${activePage}`,
        limit: "10",
      };
      if (searchValue) {
        sendData.page = "1";
        sendData.search = searchValue;
      }
      if (startDate) {
        sendData.page = "1";
        sendData.startDate = moment.tz(startDate, "YYYY-MM-DD", "Asia/Kolkata").startOf("day").toISOString();
      }
      if (endDate) {
        sendData.page = "1";
        sendData.endDate = moment.tz(endDate, "YYYY-MM-DD", "Asia/Kolkata").endOf("day").toISOString();
      }
      if (selectionStatus) {
        sendData.page = "1";
        sendData.selectionStatus = selectionStatus;
      }
      if ((searchValue || startDate || endDate || selectionStatus) && searchParam.has("page")) {
        searchParam.delete("page");
        setSearchParam(searchParam);
      }
      const { data } = await fetchAstrologerRegistrationFormApi(sendData);
      if (data.code === 200) {
        setFormList(data?.result?.list || []);
        setTotal(data?.result?.total || 0);
        scrollToUp();
      } else {
        setFormList([]);
        setTotal(0);
        toastMessage(data.message, "error", "astrologerFormList");
      }
    } catch (error) {
      setFormList([]);
      setTotal(0);
    } finally {
      hideLoader();
    }
  };

  const updateSelectionStatus = async (form, nextStatus) => {
    const currentStatus = getSelectionStatus(form);
    const selectionValue = currentStatus === nextStatus ? "pending" : nextStatus;
    if (updatingId) return;

    try {
      setUpdatingId(form._id);
      const { data } = await updateAstrologerRegistrationFormStatusApi({
        formId: form._id,
        selectionStatus: selectionValue,
      });
      if (data.code === 200) {
        const updatedForm = data.result || { ...form, selectionStatus: selectionValue };
        const nextStatus = getSelectionStatus(updatedForm);
        setFormList((prev) =>
          prev
            .map((item) => (item._id === form._id ? { ...item, ...updatedForm } : item))
            .filter((item) => !selectionStatus || getSelectionStatus(item) === selectionStatus)
        );
        if (selectionStatus && nextStatus !== selectionStatus) {
          setTotal((prev) => Math.max(0, prev - 1));
        }
        setSelectedForm((prev) => (prev?._id === form._id ? { ...prev, ...updatedForm } : prev));
        toastMessage(
          selectionValue === "selected"
            ? "Marked as selected"
            : selectionValue === "rejected"
              ? "Marked as rejected"
              : "Status set to pending",
          "success",
          "astrologerFormStatus"
        );
      } else {
        toastMessage(data.message, "error", "astrologerFormStatus");
      }
    } catch (error) {
      toastMessage("Something went wrong", "error", "astrologerFormStatus");
    } finally {
      setUpdatingId("");
    }
  };

  useEffect(() => {
    if (Number(activePage) === 1 && searchParam.has("page")) {
      searchParam.delete("page");
      setSearchParam(searchParam);
    }
    getFormList(debounceValue);
  }, [searchParam, debounceValue, startDate, endDate, selectionStatus]);

  const renderStatusActions = (form) => {
    const currentStatus = getSelectionStatus(form);
    const disabled = updatingId === form._id;
    return (
      <>
        <td onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            className="btn"
            disabled={disabled}
            style={statusButtonStyle(currentStatus === "selected", "selected")}
            onClick={() => updateSelectionStatus(form, "selected")}
          >
            Selected
          </button>
        </td>
        <td onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            className="btn"
            disabled={disabled}
            style={statusButtonStyle(currentStatus === "rejected", "rejected")}
            onClick={() => updateSelectionStatus(form, "rejected")}
          >
            Rejected
          </button>
        </td>
      </>
    );
  };

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h4 className="Title">Astrologer Registration Form</h4>
          </div>
          <div className="FilterBox FilterArea">
            <div className="form-group search_lg_size userSearch">
              <input
                type="search"
                className={`form-control ${search && "active"}`}
                placeholder="Search by name, email, phone or location"
                value={search || ""}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="user_search_icon">
                <IoSearch />
              </span>
            </div>
            <div className="form-group" style={{ minWidth: 160 }}>
              <label htmlFor="astrologer-form-status">Status</label>
              <select
                id="astrologer-form-status"
                className="form-control"
                value={selectionStatus}
                onChange={(e) => setSelectionStatus(e.target.value)}
              >
                {SELECTION_STATUS_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ minWidth: 190 }}>
              <label htmlFor="astrologer-form-start-date">Submitted from</label>
              <input
                id="astrologer-form-start-date"
                type="date"
                className="form-control"
                value={startDate}
                max={endDate || moment.tz("Asia/Kolkata").format("YYYY-MM-DD")}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ minWidth: 190 }}>
              <label htmlFor="astrologer-form-end-date">Submitted to</label>
              <input
                id="astrologer-form-end-date"
                type="date"
                className="form-control"
                value={endDate}
                min={startDate || undefined}
                max={moment.tz("Asia/Kolkata").format("YYYY-MM-DD")}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            {(startDate || endDate || selectionStatus) && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ alignSelf: "flex-end", marginBottom: 0 }}
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setSelectionStatus("");
                }}
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Contact</th>
                  <th>Location</th>
                  <th>Experience</th>
                  <th>Skills</th>
                  <th>Submitted</th>
                  <th>Selected</th>
                  <th>Rejected</th>
                </tr>
              </thead>
              <tbody>
                {formList.length > 0 ? (
                  formList.map((form, index) => (
                    <tr key={form._id} onClick={() => setSelectedForm(form)} style={{ cursor: "pointer" }}>
                      <td>{findSerialNumber(index, activePage, 10)}</td>
                      <td>
                        {form.photo ? (
                          <img
                            src={form.photo}
                            alt={form.fullName}
                            style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                          />
                        ) : (
                          "---"
                        )}
                      </td>
                      <td>{form.fullName || "---"}</td>
                      <td>{form.email || "---"}</td>
                      <td>{form.contactNumber || "---"}</td>
                      <td>{form.location || "---"}</td>
                      <td>{form.experienceInYears || "---"}</td>
                      <td>{(form.skills || []).join(", ") || "---"}</td>
                      <td>{formatDate(form.createdAt)}</td>
                      {renderStatusActions(form)}
                    </tr>
                  ))
                ) : (
                  <NotFound
                    cols={11}
                    msg="No astrologer registration found!"
                    icon={NOT_FOUND}
                    loader={loader}
                  />
                )}
              </tbody>
            </table>
          </div>
          {total > 10 ? (
            <div className="pagination-wrapper">
              <div className="Pagination">
                <Pagination
                  activePage={activePage}
                  previousLabel={"previous"}
                  nextLabel={"next"}
                  itemsCountPerPage={10}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                  hideDisabled={true}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <ModalBox
        status={Boolean(selectedForm)}
        onClose={() => setSelectedForm(null)}
        title={selectedForm?.fullName || "Astrologer details"}
        closeIcon
        size="md"
        noFooter
      >
        {selectedForm && (
          <div>
            {selectedForm.photo && (
              <img
                src={selectedForm.photo}
                alt={selectedForm.fullName}
                style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8, marginBottom: 16 }}
              />
            )}
            {selectedForm.panCard && (
              <img
                src={selectedForm.panCard}
                alt="PAN card"
                style={{ width: 180, height: 120, objectFit: "cover", borderRadius: 8, marginBottom: 16  , marginLeft : 30}}
              />
            )}
            <p><strong>Email:</strong> {selectedForm.email || "---"}</p>
            <p><strong>Contact:</strong> {selectedForm.contactNumber || "---"}</p>
            <p><strong>Date of Birth:</strong> {selectedForm.dateOfBirth || "---"}</p>
            <p><strong>Location:</strong> {selectedForm.location || "---"}</p>
            <p><strong>Experience:</strong> {selectedForm.experienceInYears || "---"}</p>
            <p><strong>Skills:</strong> {(selectedForm.skills || []).join(", ") || "---"}</p>
            <p>
              <strong>Languages:</strong>{" "}
              {[...(selectedForm.languages || []), selectedForm.otherLanguage].filter(Boolean).join(", ") || "---"}
            </p>
            <p><strong>Working platform:</strong> {selectedForm.workingPlatform || "---"}</p>
            <p><strong>Referred by:</strong> {selectedForm.referredBy || "---"}</p>
            <p><strong>Submitted:</strong> {formatDate(selectedForm.createdAt)}</p>
            <p>
              <strong>Status:</strong>{" "}
              {getSelectionStatus(selectedForm) === "selected"
                ? "Selected"
                : getSelectionStatus(selectedForm) === "rejected"
                  ? "Rejected"
                  : "Pending"}
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn"
                disabled={updatingId === selectedForm._id}
                style={statusButtonStyle(getSelectionStatus(selectedForm) === "selected", "selected")}
                onClick={() => updateSelectionStatus(selectedForm, "selected")}
              >
                Selected
              </button>
              <button
                type="button"
                className="btn"
                disabled={updatingId === selectedForm._id}
                style={statusButtonStyle(getSelectionStatus(selectedForm) === "rejected", "rejected")}
                onClick={() => updateSelectionStatus(selectedForm, "rejected")}
              >
                Rejected
              </button>
            </div>
          </div>
        )}
      </ModalBox>
    </>
  );
};

export default AstrologerRegistrationFormList;
